import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Surfaces that can take over the FOCUS PANE — the right-hand pane that pairs a
 * working surface with the conversation. Adding one is a registry entry (see
 * `focus-pane-registry.tsx`) plus an id here; no layout rewiring.
 */
export type FocusPaneId = "tasks" | "agentBrowser" | "contacts" | "agent" | "schedule";

/**
 * Operator-set bounds. The pane (RIGHT) is the work surface and the conversation
 * (LEFT) is the compact assistant beside it, so the pane opens DOMINANT: 70% of
 * the page, i.e. conversation 30% (operator 2026-07-25). At 30% the conversation
 * is always below the zoom's comfortable width, so a pane-open chat is the
 * compact miniature by default. The lower bound stays 35% so a lawyer can still
 * drag the chat back to the majority when they want it — dragging below the
 * comfort width simply re-expands the chat out of compact mode. Max lifted to
 * 80% to give headroom above the new default rather than pinning it at the edge.
 */
export const FOCUS_PANE_MIN_PCT = 35;
export const FOCUS_PANE_MAX_PCT = 80;
export const FOCUS_PANE_DEFAULT_PCT = 70;

export type AppMode =
  | "home"
  | "chat"
  | "agentBrowser"
  | "agent"
  | "cases"
  | "activity"
  | "contacts"
  | "tasks"
  | "schedule"
  | "org"
  | "settings";

interface UIState {
  activeMode: AppMode;
  /** Case-context side panel (`SidePanel.tsx`) — distinct from the
   *  new Claude-style `sidebarExpanded` below. Drives whether the
   *  per-case file tree shows when the user is inside a case route. */
  sidePanelCollapsed: boolean;
  /**
   * Timestamp of the last time the USER collapsed/expanded the side panel
   * themselves (toggle button, ⌘/Ctrl+B, the drawer's close/Esc) — `null`
   * until they do, and RESET to `null` whenever the panel is re-opened.
   *
   * Exists because `sidePanelCollapsed` is written by three AUTOMATIC paths
   * (a focus pane opening, a compact viewport, Esc in the drawer) and nothing
   * ever guaranteed it back: one agent-pane session left the navigator column
   * gone on every conversation surface, with no visible way to restore it.
   * The entry guarantee (`useSidePanelEntryGuarantee`) re-opens the panel when
   * the lawyer enters a conversation route — but ONLY when this is `null`, so
   * an explicit collapse still stands for the rest of the session. Same
   * "user intent outranks automation" pattern as `lastUserToggleAt`.
   */
  sidePanelUserCollapsedAt: number | null;
  /**
   * When the lawyer first SENT something from this browser (miniplan H2).
   *
   * The neutral-landing redirect needs to know "has this lawyer ever done
   * anything" — and the authoritative server record has a hole: the SPA never
   * persists `global` scope, so a lawyer who only ever uses the global chat
   * stays at the record's epoch default forever. This stamp closes that hole
   * locally and is the literal form of the operator's rule (2026-07-23):
   * *"repeat until user makes some kind of activity — if he exits without
   * typing anything, next time it should show welcome again."*
   *
   * Stamped on SEND, never on visit, and PERSISTED (a stamp that died with the
   * tab would re-show the welcome to someone who did type). Write-once: the
   * setter no-ops once set, so the value stays the FIRST activity, not the
   * latest — nothing reads it as a recency signal and it must not become one by
   * accident.
   */
  firstActivityAt: number | null;
  /**
   * The boot-time landing verdict (miniplan H2). `null` = still deciding.
   *
   * Ephemeral by design — NOT in the `partialize` allowlist, so every page load
   * re-decides. Two consumers:
   *   - `/home` holds its render while this is `null`, so a returning lawyer
   *     never sees the welcome flash by before the redirect lands.
   *   - the rail's Home entry (D-3) reads the verdict rather than re-deriving
   *     it: a returning lawyer whose activity lives only in the SERVER record
   *     has nothing client-side to re-derive from, and would otherwise be sent
   *     back to a first-run welcome by their own Home button.
   *
   * A timeout resolves to `"welcome"` — "we could not tell" and "first run"
   * land on the same, recoverable screen.
   */
  landing: "welcome" | "resume" | null;
  /**
   * Set while a landing redirect is IN FLIGHT — i.e. `navigate()` has been
   * called but the destination route has not taken over yet.
   *
   * This exists because of a measured behaviour, not a theoretical one: the
   * case workspace is a lazy chunk, and react-router keeps the OUTGOING route
   * mounted while it loads. Releasing the `/home` gate the moment the verdict
   * lands therefore painted the welcome for ~240ms *after* the URL had already
   * changed to `/cases/…` — the exact flash the gate was added to prevent.
   *
   * Deliberately NOT expressed as "hold whenever the verdict is `resume`":
   * `useExitCase` (the header's Sair action) and `useCaseSwitcher` navigate to
   * `/home` on purpose, and a
   * verdict-based hold would leave those two showing a blank page forever.
   * Cleared when `HomeLayout` unmounts — the flag's whole life is that one
   * suppressed render.
   */
  landingRedirecting: boolean;
  /** Claude-style left sidebar (`Sidebar.tsx` — replaces the
   *  legacy `ActivityBar` rail + the top `Header`). Persisted to
   *  `localStorage` so the user's preferred state survives reloads.
   *  Mobile ignores this flag; the `Sidebar` component derives the
   *  drawer-vs-rail mode from `useViewportSize()` directly so the
   *  store stays viewport-agnostic. */
  sidebarExpanded: boolean;
  /** Right-side `RightSidebar` (Team of Agents pane host —
   *  shell-level monitor surface, peer to the left `Sidebar`).
   *  Defaults to `false` (collapsed) per the §I.2 decision —
   *  the pane auto-expands on the first poll if active/awaiting
   *  tasks exist AND the user hasn't manually toggled yet (see
   *  `lastUserToggleAt`). Persisted across reloads. Mobile/tablet
   *  derives drawer-vs-rail from viewport, ignoring this flag —
   *  same convention as the left sidebar. See
   *  ai-docs/lexide-right-sidebar-implementation-plan.md §I.2, §I.3. */
  rightSidebarExpanded: boolean;
  /** Wall-clock ms of the lawyer's last MANUAL toggle of
   *  `rightSidebarExpanded`. `null` means "auto-expand may still
   *  fire this session"; any non-null value means the lawyer has
   *  taken control and the §I.2 auto-expand effect short-circuits.
   *  Reset to `null` only on store `clear()`. Persisted so the
   *  "user preference wins" semantics survives reloads. */
  lastUserToggleAt: number | null;
  /** Mobile/tablet drawer open state. Distinct from
   *  `rightSidebarExpanded` (which is the DESKTOP layout
   *  preference): the mobile drawer is a one-shot UI gesture, not
   *  a persistent layout choice. Resetting per-session (NOT
   *  persisted) avoids two confused behaviours:
   *    1. A user who expanded the pane on desktop then later loads
   *       LexIDE on a phone would otherwise see the drawer pop
   *       open over the route on first paint.
   *    2. Mobile drawer taps would otherwise stamp
   *       `lastUserToggleAt` and permanently disable the §I.2
   *       desktop auto-expand for that user.
   *  See review/wf07tgf9h findings M1/M2. */
  rightDrawerOpen: boolean;

  /**
   * The Pendências side pane (LexIDE only — it is NOT part of the shared
   * `@lexiun/tasklist-core` surface; LexPortal has no equivalent).
   *
   * Opening it FOCUSES the screen: the nav rail drops to its compact width,
   * the file/case column and the Agents pane hide, so the lawyer is left with
   * the conversation on one side and the tasklist on the other — the point
   * being to drive the list by prompt ("descarte as pendências deste cliente")
   * while watching it change.
   *
   * `tasklistPaneRestore` snapshots what those three panes looked like BEFORE
   * the focus, so closing puts the workspace back exactly as it was rather
   * than leaving the lawyer to rebuild their layout. Deliberately NOT
   * persisted: a snapshot that outlived a reload would restore stale state
   * over whatever the user set up in the meantime.
   */
  /**
   * Which surface is open in the FOCUS PANE, or `null`. See
   * `components/shell/focus-pane-registry.tsx` for the surfaces.
   */
  focusPane: FocusPaneId | null;
  /**
   * Layout captured before the focus, restored on close.
   *
   * NOT including `sidebarExpanded`: the nav rail is owned by
   * `useRailSpaceGovernor`, because the rule is broader than this pane — the
   * rail collapses whenever ANY space-competing surface is up (focus pane,
   * file viewer, or the Agents sidebar).
   */
  focusPaneRestore: {
    sidePanelCollapsed: boolean;
    rightSidebarExpanded: boolean;
  } | null;
  /** Pane width as a % of the viewport, clamped to [35, 65]. Persisted. */
  focusPaneWidthPct: number;
  /**
   * ONE-SHOT section handoff for the agent pane.
   *
   * A focus pane has no URL, so a deep link like `/agent?section=email` has
   * nowhere to put its intent. The `/agent` entry writes it here immediately
   * before `openFocusPane("agent")`; `AgentPaneSurface` reads it once on mount
   * and clears it. Deliberately NOT persisted and NOT a general payload bus —
   * one nullable field, consumed exactly once, so a stale value can never
   * re-select a tab on some later unrelated open.
   */
  agentPaneInitial: { section: string; tab?: string } | null;
  setAgentPaneInitial: (v: { section: string; tab?: string } | null) => void;
  /**
   * ONE-SHOT case scope for the tasks pane — same shape and same reasoning as
   * `agentPaneInitial` above.
   *
   * The case header's "N pendências" signal is a claim ABOUT ONE CASE, so
   * clicking it must land on that case's pendências, not on the global list
   * where the lawyer would have to find them again. The header writes the id
   * immediately before `openFocusPane("tasks")`; the surface reads it on mount.
   *
   * Deliberately NOT persisted and NOT a general payload bus: one nullable
   * field, so a stale value can never silently scope some later unrelated open
   * to a case the lawyer has since left.
   */
  tasksPaneCaseId: string | null;
  setTasksPaneCaseId: (v: string | null) => void;
  /** Open the tasks pane SCOPED to one case (the header's pendências backlink). */
  openTasksPaneForCase: (caseId: string) => void;
  openFocusPane: (id: FocusPaneId) => void;
  closeFocusPane: () => void;
  toggleFocusPane: (id: FocusPaneId) => void;
  setFocusPaneWidthPct: (pct: number) => void;

  setActiveMode: (mode: AppMode) => void;
  /** USER-initiated toggle — stamps `sidePanelUserCollapsedAt` when it
   *  collapses, so the entry guarantee stops re-opening for the session. */
  toggleSidePanel: () => void;
  /** AUTOMATIC setter — does NOT record user intent. Used by the focus pane,
   *  the compact-viewport effect and the entry guarantee. A user-initiated
   *  collapse must go through `toggleSidePanel`/`setSidePanelCollapsedByUser`
   *  instead, or the guarantee will override it on the next route entry. */
  setSidePanelCollapsed: (collapsed: boolean) => void;
  /** USER-initiated setter, for call sites that represent a deliberate act
   *  (the drawer's close button / Esc). Same intent-stamping as the toggle. */
  setSidePanelCollapsedByUser: (collapsed: boolean) => void;
  /** Record that the lawyer SENT something. Idempotent + write-once — safe to
   *  call on every send (which is where it lives, in `useChatSession.send`). */
  markFirstActivity: () => void;
  /** Called once per boot by `LastActiveContextSync` when the landing question
   *  is answered — including when it is answered by giving up (timeout /
   *  offline), so `/home` can never be held behind an unreachable server.
   *  First write wins, so the timeout cannot overwrite a real verdict that
   *  landed in the same tick. */
  setLanding: (verdict: "welcome" | "resume") => void;
  /** Mark a landing redirect as issued (holds `/home` blank through the route
   *  transition) / released (on `HomeLayout` unmount). */
  setLandingRedirecting: (v: boolean) => void;
  toggleSidebar: () => void;
  setSidebarExpanded: (expanded: boolean) => void;
  /** Manual toggle by the lawyer — stamps `lastUserToggleAt` so the
   *  §I.2 auto-expand effect never re-fires this session. */
  toggleRightSidebar: () => void;
  /** Manual setter (also stamps `lastUserToggleAt`). For programmatic
   *  use by code paths that represent a user-initiated action — the
   *  signalling-tool hooks (`useTeamPaneCommandsFromAgent`) use this
   *  too because a concierge-driven nudge is a user-initiated
   *  semantic event. */
  setRightSidebarExpanded: (expanded: boolean) => void;
  /** Auto-expand setter — does NOT stamp `lastUserToggleAt`. Used by
   *  the §I.2 first-poll auto-expand effect inside `TeamOfAgentsPane`
   *  so the auto-expand doesn't poison the user-toggle sentinel.
   *  Short-circuits if `lastUserToggleAt` is already non-null. */
  setRightSidebarExpandedAuto: (expanded: boolean) => void;
  setRightDrawerOpen: (open: boolean) => void;
  toggleRightDrawer: () => void;
  // ── Detail dialogs (plan §K-bis; commit 14ad0be7) ─────────────────────
  /** Which mission's detail dialog is open. `null` = closed. At most
   *  one detail dialog (mission OR harness) is open at a time —
   *  opening one closes the other. */
  openMissionDetailId: string | null;
  /** Which standalone-harness task's detail dialog is open. */
  openHarnessDetailTaskId: string | null;
  openMissionDetail: (missionId: string) => void;
  openHarnessDetail: (taskId: string) => void;
  closeDetailDialog: () => void;
  // ── Pane widths (operator directive 2026-06-24 — both shells horizontally
  //    resizable; case-workspace pane only WIDER than the starter, right
  //    sidebar down to 100px). Persisted across reloads. Drawer modes
  //    (mobile/compact) ignore these — width is viewport-derived there.
  // ─────────────────────────────────────────────────────────────────────
  /** Desktop width of the left case-workspace pane (`SidePanel`). Default
   *  `SIDE_PANEL_DEFAULT_PX` (= 288, the historical `w-72`). Min equals
   *  the default — the operator's rule "to a wider size, not below the
   *  minimum width". Max clamps to `SIDE_PANEL_MAX_PX`. */
  sidePanelWidth: number;
  /** Desktop width of the right `RightSidebar` (Lexiun Agents pane).
   *  Default `RIGHT_SIDEBAR_DEFAULT_PX` (= 256). Min `RIGHT_SIDEBAR_MIN_PX`
   *  (= 100, operator spec — they want the option to shrink it small).
   *  Max `RIGHT_SIDEBAR_MAX_PX`. */
  rightSidebarWidth: number;
  setSidePanelWidth: (px: number) => void;
  setRightSidebarWidth: (px: number) => void;
  /** Vertical split ratio inside the SidePanel between the top pane
   *  (case-workspace file tree) and the bottom pane (case navigator).
   *  Range `[SIDEBAR_SPLIT_MIN, SIDEBAR_SPLIT_MAX]` — clamped by
   *  `setSidebarSplitRatio`. Default `SIDEBAR_SPLIT_DEFAULT` (0.75 —
   *  75% workspace / 25% navigator, per operator brief 2026-07-01).
   *  Persisted like the pane widths so the lawyer's chosen split
   *  survives reloads. `1.0` is NOT allowed (would hide the navigator);
   *  `0.0` neither (would hide the workspace tree). */
  sidebarSplitRatio: number;
  setSidebarSplitRatio: (ratio: number) => void;

  /** User-dragged height of the CASOS pane (`CaseNavigator`'s bottom
   *  section), in px. `null` = AUTO — the content-sized, `max-h`-capped
   *  default from the 2026-07-03 brief, where the file tree is the star and
   *  the cases pane shrink-wraps its content.
   *
   *  The `null` default matters: it keeps that brief exactly true for anyone
   *  who never touches the handle, so making the pane resizable (operator
   *  2026-07-27) costs ZERO visual change until a deliberate drag. Reset to
   *  `null` by double-clicking the handle.
   *
   *  Persisted like the pane widths. Clamped by `clampCasesPaneHeight` on
   *  write AND on rehydration. */
  casesPaneHeight: number | null;
  setCasesPaneHeight: (px: number | null) => void;
}

// Width bounds — exported so the panes themselves can echo them in the
// resize-handle's aria attributes and the consumer can verify clamping
// in unit tests.
export const SIDE_PANEL_DEFAULT_PX = 288;
export const SIDE_PANEL_MIN_PX = 288;
export const SIDE_PANEL_MAX_PX = 640;
export const RIGHT_SIDEBAR_DEFAULT_PX = 256;
export const RIGHT_SIDEBAR_MIN_PX = 100;
export const RIGHT_SIDEBAR_MAX_PX = 640;

// Sidebar-internal vertical split (top: case-workspace / bottom: case
// navigator). Exposed so the drag handle can echo them in aria + tests
// can exercise the clamp directly.
export const SIDEBAR_SPLIT_MIN = 0.25;
export const SIDEBAR_SPLIT_MAX = 0.85;
export const SIDEBAR_SPLIT_DEFAULT = 0.75;

function clampSidePanelWidth(px: number): number {
  if (!Number.isFinite(px)) return SIDE_PANEL_DEFAULT_PX;
  return Math.max(SIDE_PANEL_MIN_PX, Math.min(SIDE_PANEL_MAX_PX, Math.round(px)));
}
function clampRightSidebarWidth(px: number): number {
  if (!Number.isFinite(px)) return RIGHT_SIDEBAR_DEFAULT_PX;
  return Math.max(RIGHT_SIDEBAR_MIN_PX, Math.min(RIGHT_SIDEBAR_MAX_PX, Math.round(px)));
}
/** Clamp the split ratio to a usable band — neither pane collapses to
 *  zero, and the drag can't push the navigator smaller than a case row
 *  or the workspace tree smaller than a folder row. Rejects non-finite
 *  input (returns default). Exported for unit testing the pure logic. */
export function clampSidebarSplit(ratio: number): number {
  if (!Number.isFinite(ratio)) return SIDEBAR_SPLIT_DEFAULT;
  return Math.max(SIDEBAR_SPLIT_MIN, Math.min(SIDEBAR_SPLIT_MAX, ratio));
}

/** Floor for the dragged CASOS pane: the sticky reverse bar (~28px) plus one
 *  22px case row plus its padding. Below this the pane would be a headless bar
 *  — visible chrome with nothing in it, which reads as broken rather than
 *  collapsed. */
export const CASES_PANE_MIN_PX = 72;
/** Ceiling as a share of the SidePanel column. The file tree is the star
 *  (2026-07-03 brief); at 75% the tree still keeps a usable quarter, and the
 *  drag simply stops rather than letting the tree vanish. */
export const CASES_PANE_MAX_RATIO = 0.75;

/**
 * Clamp a dragged CASOS-pane height. `null` passes through untouched — it is
 * the meaningful AUTO sentinel, not a missing value.
 *
 * `columnPx` is the SidePanel column height measured at drag time; the ceiling
 * is relative because the window resizes. When it is unknown or degenerate
 * (0 during the first paint, or a non-finite value) only the floor applies —
 * clamping against a bogus ceiling would snap a legitimate height to 0.
 *
 * Exported for unit testing the pure logic.
 */
export function clampCasesPaneHeight(px: number | null, columnPx?: number): number | null {
  if (px === null) return null;
  if (!Number.isFinite(px)) return null; // corrupted → fall back to AUTO
  const floored = Math.max(CASES_PANE_MIN_PX, Math.round(px));
  if (!Number.isFinite(columnPx as number) || !columnPx || columnPx <= 0) return floored;
  const ceiling = Math.max(CASES_PANE_MIN_PX, Math.round(columnPx * CASES_PANE_MAX_RATIO));
  return Math.min(floored, ceiling);
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      activeMode: "home",
      sidePanelCollapsed: false,
      sidePanelUserCollapsedAt: null,
      firstActivityAt: null,
      landing: null,
      landingRedirecting: false,
      sidebarExpanded: true,
      // §I.2: smart auto-expand. Initial value is `false` (collapsed)
      // so a brand-new session doesn't flash the pane open before
      // the first poll's auto-expand effect has fired. Persisted, so
      // a returning user gets back to whatever they last had.
      rightSidebarExpanded: false,
      lastUserToggleAt: null,
      rightDrawerOpen: false,
      setActiveMode: (mode) => set({ activeMode: mode }),
      // USER intent: a deliberate collapse is remembered for the session (the
      // entry guarantee then leaves it alone); re-opening CLEARS the memory, so
      // the lawyer is never one collapse away from losing the guarantee forever.
      toggleSidePanel: () =>
        set((s) => {
          const collapsed = !s.sidePanelCollapsed;
          return {
            sidePanelCollapsed: collapsed,
            sidePanelUserCollapsedAt: collapsed ? Date.now() : null,
          };
        }),
      // Write-once. `send` fires this on EVERY message, so guarding here keeps
      // the field meaning "first activity" instead of quietly turning into
      // "last activity" — and skips a store write (plus its localStorage
      // round-trip) on every subsequent send.
      markFirstActivity: () =>
        set((s) => (s.firstActivityAt == null ? { firstActivityAt: Date.now() } : s)),
      setLanding: (verdict) => set((s) => (s.landing == null ? { landing: verdict } : s)),
      setLandingRedirecting: (v) => set({ landingRedirecting: v }),
      setSidePanelCollapsedByUser: (collapsed) =>
        set({
          sidePanelCollapsed: collapsed,
          sidePanelUserCollapsedAt: collapsed ? Date.now() : null,
        }),
      // AUTOMATIC: deliberately leaves `sidePanelUserCollapsedAt` untouched —
      // the focus pane and the compact-viewport effect are not the lawyer
      // asking for a narrower screen.
      setSidePanelCollapsed: (collapsed) => set({ sidePanelCollapsed: collapsed }),
      toggleSidebar: () => set((s) => ({ sidebarExpanded: !s.sidebarExpanded })),
      setSidebarExpanded: (expanded) => set({ sidebarExpanded: expanded }),
      toggleRightSidebar: () =>
        set((s) => ({
          rightSidebarExpanded: !s.rightSidebarExpanded,
          lastUserToggleAt: Date.now(),
        })),
      setRightSidebarExpanded: (expanded) =>
        set({ rightSidebarExpanded: expanded, lastUserToggleAt: Date.now() }),
      setRightSidebarExpandedAuto: (expanded) =>
        set((s) => {
          // §I.2: auto-expand short-circuits once the user has
          // manually toggled this session — their preference wins.
          if (s.lastUserToggleAt !== null) return s;
          if (s.rightSidebarExpanded === expanded) return s;
          return { rightSidebarExpanded: expanded };
        }),
      focusPane: null,
      focusPaneRestore: null,
      focusPaneWidthPct: FOCUS_PANE_DEFAULT_PCT,
      agentPaneInitial: null,
      setAgentPaneInitial: (v) => set({ agentPaneInitial: v }),
      tasksPaneCaseId: null,
      setTasksPaneCaseId: (v) => set({ tasksPaneCaseId: v }),
      openFocusPane: (id) =>
        set((s) => {
          // Any ORDINARY open clears the tasks scope. Without this, a stale id
          // from an earlier header backlink would silently scope a later global
          // open to a case the lawyer has since left — the failure the one-shot
          // comment warns about. `openTasksPaneForCase` is the one path that
          // sets it, and it sets it AFTER this runs.
          // Switching between surfaces while already focused must NOT
          // re-snapshot: the second snapshot would capture the COLLAPSED
          // layout, and closing would then "restore" the focus state forever.
          if (s.focusPane !== null) return { focusPane: id, tasksPaneCaseId: null };
          return {
            focusPane: id,
            tasksPaneCaseId: null,
            focusPaneRestore: {
              sidePanelCollapsed: s.sidePanelCollapsed,
              rightSidebarExpanded: s.rightSidebarExpanded,
            },
            sidePanelCollapsed: true,
            rightSidebarExpanded: false,
          };
        }),
      /** The header's "N pendências" backlink. ONE action, so the scope and the
       *  open cannot be reordered or half-applied — setting the id and opening
       *  the pane as two calls raced with `openFocusPane`'s own clear. */
      openTasksPaneForCase: (caseId) => {
        get().openFocusPane("tasks");
        set({ tasksPaneCaseId: caseId });
      },
      closeFocusPane: () =>
        set((s) => {
          if (s.focusPane === null) return {};
          const r = s.focusPaneRestore;
          return {
            focusPane: null,
            focusPaneRestore: null,
            // No snapshot (state rehydrated mid-session) → leave the panes
            // where they are rather than guessing at defaults.
            ...(r
              ? {
                  sidePanelCollapsed: r.sidePanelCollapsed,
                  rightSidebarExpanded: r.rightSidebarExpanded,
                }
              : {}),
          };
        }),
      toggleFocusPane: (id) => {
        const { focusPane, openFocusPane, closeFocusPane } = get();
        // Same surface → close. Different surface → swap without un-focusing.
        if (focusPane === id) closeFocusPane();
        else openFocusPane(id);
      },
      setFocusPaneWidthPct: (pct) =>
        set({
          focusPaneWidthPct: Math.min(
            FOCUS_PANE_MAX_PCT,
            Math.max(FOCUS_PANE_MIN_PCT, pct),
          ),
        }),
      setRightDrawerOpen: (open) => set({ rightDrawerOpen: open }),
      toggleRightDrawer: () =>
        set((s) => ({ rightDrawerOpen: !s.rightDrawerOpen })),
      openMissionDetailId: null,
      openHarnessDetailTaskId: null,
      openMissionDetail: (missionId) =>
        set({ openMissionDetailId: missionId, openHarnessDetailTaskId: null }),
      openHarnessDetail: (taskId) =>
        set({ openMissionDetailId: null, openHarnessDetailTaskId: taskId }),
      closeDetailDialog: () =>
        set({ openMissionDetailId: null, openHarnessDetailTaskId: null }),
      sidePanelWidth: SIDE_PANEL_DEFAULT_PX,
      rightSidebarWidth: RIGHT_SIDEBAR_DEFAULT_PX,
      sidebarSplitRatio: SIDEBAR_SPLIT_DEFAULT,
      // AUTO by default — see the field doc. The pane is resizable, but nothing
      // moves until the lawyer drags it.
      casesPaneHeight: null,
      // The column height is not known here, so only the floor applies on write;
      // the drag handle passes the measured column and pre-clamps against the
      // ceiling before calling this.
      setCasesPaneHeight: (px) => set({ casesPaneHeight: clampCasesPaneHeight(px) }),
      setSidePanelWidth: (px) =>
        set({ sidePanelWidth: clampSidePanelWidth(px) }),
      setRightSidebarWidth: (px) =>
        set({ rightSidebarWidth: clampRightSidebarWidth(px) }),
      setSidebarSplitRatio: (ratio) =>
        set({ sidebarSplitRatio: clampSidebarSplit(ratio) }),
    }),
    {
      name: "lexide:ui",
      storage: createJSONStorage(() => localStorage),
      // Persist the two sidebar preferences + the user-toggle
      // sentinel. `activeMode` is derived from the URL on every mount
      // (see `ActivityBar`/`Sidebar`'s `modeFromPath`);
      // `sidePanelCollapsed` is per-session ephemeral and should
      // reset on reload.
      partialize: (state) => ({
        sidebarExpanded: state.sidebarExpanded,
        rightSidebarExpanded: state.rightSidebarExpanded,
        lastUserToggleAt: state.lastUserToggleAt,
        // The first-activity stamp MUST outlive the tab: it is the whole reason
        // a lawyer who has typed stops getting the first-run welcome. Note the
        // deliberate absence of `landingResolved` — that one is per-boot and
        // persisting it would skip the redirect on every reload after the first.
        firstActivityAt: state.firstActivityAt,
        // Pane widths persist so the lawyer's resize stays put across
        // reloads (matches the sidebar expansion preference's behaviour).
        sidePanelWidth: state.sidePanelWidth,
        rightSidebarWidth: state.rightSidebarWidth,
        sidebarSplitRatio: state.sidebarSplitRatio,
        // The dragged cases-pane height persists like the pane widths — a
        // layout choice the lawyer made deliberately should survive a reload.
        casesPaneHeight: state.casesPaneHeight,
        // Width persists like the other pane widths; the OPEN state and the
        // restore snapshot deliberately do not (a snapshot outliving a reload
        // would restore stale layout over whatever the user set up since).
        focusPaneWidthPct: state.focusPaneWidthPct,
      }),
      // Clamp size-bearing values on rehydration too — write-time
      // clamps (setSidePanelWidth / setRightSidebarWidth /
      // setSidebarSplitRatio) protect newly-written values, but
      // localStorage can carry corrupted / legacy / manually-edited
      // values that would otherwise boot the SPA into a degenerate
      // layout (e.g. `flexBasis: NaN%` from a bad `sidebarSplitRatio`).
      // Merge deep-copies the default state, then applies the persisted
      // fields with the same clamps the write path uses.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<UIState>;
        return {
          ...current,
          ...p,
          sidePanelWidth:
            p.sidePanelWidth != null
              ? clampSidePanelWidth(p.sidePanelWidth)
              : current.sidePanelWidth,
          rightSidebarWidth:
            p.rightSidebarWidth != null
              ? clampRightSidebarWidth(p.rightSidebarWidth)
              : current.rightSidebarWidth,
          sidebarSplitRatio:
            p.sidebarSplitRatio != null
              ? clampSidebarSplit(p.sidebarSplitRatio)
              : current.sidebarSplitRatio,
          // `null` is MEANINGFUL here (= AUTO), so `!= null` is the right guard:
          // an explicitly-persisted null must survive as null rather than being
          // treated as "absent" and reset to the default (which is also null,
          // but would silently diverge if that default ever changed).
          casesPaneHeight:
            p.casesPaneHeight !== undefined
              ? clampCasesPaneHeight(p.casesPaneHeight)
              : current.casesPaneHeight,
        };
      },
    },
  ),
);

// DEV-only read accessor for E2E + chrome-devtools-mcp verifies — mirrors
// the existing pattern in useCaseWorkspaceStore / useMissionsStore /
// useWorkspaceFileStore. Concierge slash-commands Part A (NAV + Agents
// hooks) checks store state by reading `window.__uiStore.getState()` to
// confirm a session.tool dispatch landed. Stripped from production
// bundles by Vite dead-code elimination when `import.meta.env.DEV` is
// `false`.
if (typeof window !== "undefined" && import.meta.env.DEV) {
  Object.defineProperty(window, "__uiStore", {
    value: useUIStore,
    writable: false,
    enumerable: false,
    configurable: true,
  });
}
